/**
 * Renders the Insights chart share snapshot to a 1080 × 1080 PNG blob.
 *
 * The layout lives in `<ChartSnapshot>` (CSS modules, Popcorn primitives,
 * real brand fonts). Chart data is passed through props and re-rendered
 * fresh by Recharts at the snapshot card's dimensions, so the captured
 * chart is sized for social aspect ratios rather than the live UI's wider
 * dimensions. At capture time we mount the snapshot into an offscreen DOM
 * container, await fonts + the avatar image, then use `html-to-image` to
 * serialise the DOM into a PNG.
 *
 * Replaces the legacy canvas-drawing + DOM-clone hybrid so design changes
 * are made in JSX + CSS instead of via canvas math or fragile SVG patching.
 */

import { createRoot } from 'react-dom/client'
import { flushSync } from 'react-dom'
import { toBlob } from 'html-to-image'

import { ChartSnapshot } from './components/ChartSnapshot'
import type { ChartSnapshotProps } from './components/ChartSnapshot'
import { loadSnapshotFontEmbedCSS } from './snapshotFontEmbedCSS'

export interface CaptureChartOptions extends ChartSnapshotProps {}

const CANVAS_SIZE = 1080
const AVATAR_PROXY_BASE = 'https://images.buffer.com/proxy/280x280/'

/** Same proxy treatment as the summary capture — see summaryCardToImage. */
function buildAvatarProxyUrl(src: string | undefined): string | undefined {
  if (!src) return undefined
  if (src.startsWith(AVATAR_PROXY_BASE)) return src
  return `${AVATAR_PROXY_BASE}${src}`
}

function waitForImages(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll('img'))
  if (imgs.length === 0) return Promise.resolve()
  return Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve()
      return new Promise<void>((resolve) => {
        img.addEventListener('load', () => resolve(), { once: true })
        img.addEventListener('error', () => resolve(), { once: true })
      })
    }),
  ).then(() => undefined)
}

/**
 * Recharts' `ResponsiveContainer` measures its parent via a `ResizeObserver`
 * on first paint. When the snapshot is rendered offscreen via `flushSync`
 * the first observation can land before layout settles, leaving the SVG at
 * 0×0. Waiting two animation frames is enough for the container to remeasure
 * and Recharts to commit the laid-out chart.
 */
function awaitTwoFrames(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })
}

export async function captureChartAsBlob(
  options: CaptureChartOptions,
): Promise<{ blob: Blob; width: number; height: number }> {
  const container = document.createElement('div')
  const containerStyle: Partial<CSSStyleDeclaration> = {
    height: `${CANVAS_SIZE}px`,
    left: '-99999px',
    pointerEvents: 'none',
    position: 'fixed',
    top: '0',
    width: `${CANVAS_SIZE}px`,
    zIndex: '-1',
  }
  Object.assign(container.style, containerStyle)
  document.body.appendChild(container)

  const root = createRoot(container)
  try {
    flushSync(() => {
      root.render(
        <ChartSnapshot
          {...options}
          channelAvatarUrl={buildAvatarProxyUrl(options.channelAvatarUrl)}
        />,
      )
    })

    if (typeof document.fonts?.ready?.then === 'function') {
      await document.fonts.ready
    }
    await waitForImages(container)
    await awaitTwoFrames()

    const target = container.firstElementChild
    if (!(target instanceof HTMLElement)) {
      throw new Error('Chart snapshot failed to mount')
    }

    // No `backgroundColor` option — the snapshot's root element has
    // `background: var(--insights-snapshot-bg)` in CSS, so html-to-image's
    // canvas-fallback colour is redundant and would just duplicate the
    // hex outside the CSS module.
    //
    // `fontEmbedCSS` short-circuits html-to-image's default font discovery,
    // which throws `SecurityError` on cross-origin stylesheets (Google
    // Fonts). When the pre-computed CSS is unavailable, fall back to
    // `skipFonts` so we still avoid the discovery path — see CORE-5602.
    const fontEmbedCSS = await loadSnapshotFontEmbedCSS()
    const blob = await toBlob(target, {
      cacheBust: false,
      height: CANVAS_SIZE,
      pixelRatio: 1,
      width: CANVAS_SIZE,
      ...(fontEmbedCSS != null ? { fontEmbedCSS } : { skipFonts: true }),
    })
    if (!blob) throw new Error('Failed to capture chart snapshot blob')

    return { blob, width: CANVAS_SIZE, height: CANVAS_SIZE }
  } finally {
    root.unmount()
    container.remove()
  }
}
